import React, { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Navigate, Route, Routes } from 'react-router-dom';
import { getAppUser, setAppUser, useAppContext, useAuth } from './contexts/AppContext';
import SharedContext from './contexts/SharedContext';
import FullLayout from './layout/FullLayout';
import LoginLayout from './layout/LoginLayout';
import LoginPage from './auth/pages/LoginPage';
import ProductiveUnitInfoPage from './public/pages/ProductiveUnitInfoPage';
import { LoadingProgress } from './lib/components/LoadingProgress/LoadingProgress';
import { getDivisionUserList, getUser } from './services/organizationService';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { useLang } from './lib/i18n';
import { enUS as enLocale } from 'date-fns/locale';
import { es as esLocale } from 'date-fns/locale';
import { paths } from './utils/paths';
import { ThemeProvider } from '@mui/material/styles';
import { AppTheme, createAppTheme } from './theme';
import { CssBaseline } from '@mui/material';



const localeMap: any = {
    en: enLocale,
    es: esLocale,
};

const HomePage = lazy(() => import('./general/pages/HomePage'));
const ChangePasswordPage = lazy(() => import('./auth/pages/ChangePasswordPage'));
const HarvestListPage = lazy(() => import('./productiveUnit/pages/HarvestListPage'));
const ProductiveUnitListPage = lazy(() => import('./productiveUnit/pages/ProductiveUnitListPage'));
const ProductiveUnitAdminPage = lazy(() => import('./productiveUnit/pages/ProductiveUnitPage'));
const ProductiveUnitNewPage = lazy(() => import('./productiveUnit/pages/ProductiveUnitNewPage'));
const EventListPage = lazy(() => import('./event/pages/EventListPage'));
const TechnicalMonitoring = lazy(() => import('./event/pages/EventTechnicalMonitoringPage'));
const EventPrescriptionPage = lazy(() => import('./event/pages/EventPrescriptionPage'));
const Event = lazy(() => import('./event/pages/EventPage'));
const EventNewPage = lazy(() => import('./event/pages/EventNewPage'));
const ReportListPage = lazy(() => import('./report/pages/ReportListPage'));
const ReportPage = lazy(() => import('./report/pages/ReportPage'));
const MapViewerPage = lazy(() => import('./mapViewer/pages/MapViewerPage'));
const BaselineEnvironmentListPage = lazy(() => import('./environment/pages/BaselineEnvironmentListPage'));
const BaselineEnvironmentNewPage = lazy(() => import('./environment/pages/BaselineEnvironmentNewPage'));
const BaselineEnvironmentPage = lazy(() => import('./environment/pages/BaselineEnvironmentPage'));
const BaselineEnvironmentCompletedPage = lazy(() => import('./environment/pages/BaselineEnvironmentCompletedPage'));

function App() {
    const logged = useAuth();
    const [userLoaded, setUserLoaded] = React.useState<boolean>(false);
    const lang = useLang();
    const [theme, setTheme] = React.useState<AppTheme>(createAppTheme(false));
    const { darkTheme } = useAppContext();

    // On refresh page, we look for the user info
    useEffect(() => {
        if (getAppUser() == null && logged) {
            getUser().then(user => {
                getDivisionUserList().then(divisions => {
                    user.divisions = divisions;
                    setAppUser(user);
                    setUserLoaded(true);
                });
            });
        } else {
            setUserLoaded(true);
        }
    }, [logged]);

    useEffect(() => {
        let theme = createAppTheme((darkTheme));
        setTheme(theme);

    }, [darkTheme])

    

    const full =
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <Router>
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={localeMap[lang]}>
                    <SharedContext>
                            <FullLayout>
                                <Routes>
                                    <Route path={paths.index} element={<Suspense fallback={<LoadingProgress />}><HomePage /></Suspense>} />
                                    <Route path={paths.home} element={<Suspense fallback={<LoadingProgress />}><HomePage /></Suspense>} />
                                    <Route path={paths.changePassword} element={<Suspense fallback={<LoadingProgress/>}><ChangePasswordPage/></Suspense>} />
                                    <Route path={paths.harvests} element={<Suspense fallback={<LoadingProgress />}><HarvestListPage /></Suspense>} />
                                    <Route path={paths.productiveUnits} element={<Suspense fallback={<LoadingProgress />}><ProductiveUnitListPage /></Suspense>} />
                                    <Route path={paths.productiveUnit} element={<Suspense fallback={<LoadingProgress />}><ProductiveUnitNewPage /></Suspense>} />
                                    <Route path={paths.productiveUnitInfo} element={<Suspense fallback={<LoadingProgress />}><ProductiveUnitAdminPage /></Suspense>} />
                                    <Route path={paths.events} element={<Suspense fallback={<LoadingProgress />}><EventListPage /></Suspense>} />
                                    <Route path={paths.event} element={<Suspense fallback={<LoadingProgress />}><Event /></Suspense>} />
                                    <Route path={paths.newEvent} element={<Suspense fallback={<LoadingProgress />}><EventNewPage /></Suspense>} />
                                    <Route path={paths.technicalMonitoring} element={<Suspense fallback={<LoadingProgress />}><TechnicalMonitoring /></Suspense>} />
                                    <Route path={paths.eventPrescription} element={<Suspense fallback={<LoadingProgress />}><EventPrescriptionPage /></Suspense>} />
                                    <Route path={paths.reports} element={<Suspense fallback={<LoadingProgress />}><ReportListPage /></Suspense>} />
                                    <Route path={paths.report} element={<Suspense fallback={<LoadingProgress />}><ReportPage /></Suspense>} />
                                    <Route path={paths.mapViewer} element={<Suspense fallback={<LoadingProgress />}><MapViewerPage /></Suspense>} />
                                    <Route path={paths.baselineEnvironments} element={<Suspense fallback={<LoadingProgress />}><BaselineEnvironmentListPage /></Suspense>} />
                                    <Route path={paths.newBaselineEnvironment} element={<Suspense fallback={<LoadingProgress />}><BaselineEnvironmentNewPage /></Suspense>} />
                                    <Route path={paths.baselineEnvironment} element={<Suspense fallback={<LoadingProgress />}><BaselineEnvironmentPage /></Suspense>} />
                                    <Route path={paths.baselineEnvironmentCompleted} element={<Suspense fallback={<LoadingProgress />}><BaselineEnvironmentCompletedPage /></Suspense>} />
                                    <Route path="*" element={<Navigate to={paths.home} replace />} />
                                </Routes>
                            </FullLayout>
                    </SharedContext>
                </LocalizationProvider>
            </Router>
        </ThemeProvider>

    const login =
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <Router>
                    <LoginLayout>
                        <Routes>
                            <Route path={paths.index} element={<LoginPage />} />
                            <Route path={paths.login} element={<LoginPage />} />
                            <Route path={paths.publicUpInfo} element={<ProductiveUnitInfoPage />} />
                            <Route path="*" element={<Navigate to={paths.login} replace />} />
                        </Routes>
                    </LoginLayout>
                
            </Router>
        </ThemeProvider>

    const loading =
        <LoadingProgress></LoadingProgress>

    if (logged) {
        if (userLoaded) {
            return full;
        }
        return loading;
    } else {
        return login;
    }

}

export default App;
